import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { useParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useListLocationIncidentsQuery, useGetDashboardStatsQuery } from '../../store/api/localApi';
import { useLocationContext } from '../../hooks/useLocationContext';
import { useAuth } from '../../hooks/useAuth';
import { ScopedLink } from '../../components/ScopedLink';
import IncidentSeverityChip from '../../components/incidents/IncidentSeverityChip';
import IncidentCreateModal from '../../components/incidents/IncidentCreateModal';
import NotifiableOverdueBanner from '../../components/incidents/NotifiableOverdueBanner';
import { IncidentSeverity, IncidentType, StaffIncident, StaffIncidentStatus } from '../../types';

const INCIDENT_TYPE_LABELS: Record<IncidentType, string> = {
  [IncidentType.ACCIDENT]: 'Accident',
  [IncidentType.INJURY]: 'Injury',
  [IncidentType.ILLNESS]: 'Illness',
  [IncidentType.MEDICATION_ERROR]: 'Medication error',
  [IncidentType.BEHAVIOURAL]: 'Behavioural',
  [IncidentType.OTHER]: 'Other',
};

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/** A NOTIFIABLE incident whose Tusla deadline has passed and hasn't been
 *  reported yet — the row-level counterpart to the location-wide
 *  `notifiableOverdue` dashboard flag that drives the banner above the
 *  table. Computed from fields already on the row (no extra fetch). */
function isRowOverdue(incident: StaffIncident): boolean {
  return (
    incident.severity === IncidentSeverity.NOTIFIABLE &&
    !incident.tuslaReportedAt &&
    !!incident.tuslaDeadlineAt &&
    new Date(incident.tuslaDeadlineAt) < new Date()
  );
}

/**
 * GET /locations/{locationId}/incidents — the staff/admin incident
 * register. This is the inspector-facing surface of the whole feature, so
 * two things are non-negotiable here (see Task 22 brief):
 *
 * 1. WITHDRAWN incidents stay visible, flagged. They are hidden only from
 *    parents (`list-my-child-incidents` filters them server-side) — the
 *    register never does, and this page never filters them out
 *    client-side either. A register that quietly drops withdrawn records
 *    is not a register.
 * 2. Notifiable-overdue rows get visual prominence (a rose left-border +
 *    tint), and `NotifiableOverdueBanner` renders above the table whenever
 *    the location has ANY overdue notifiable incident (sourced from the
 *    same `GET /dashboard/stats.notifiableOverdue` flag the dashboard
 *    itself uses — there is no separate register-scoped endpoint for it).
 *
 * All filters (fromDate/toDate/severity/incidentType/status/childId) are
 * passed straight through as query params — `listForLocation` evaluates
 * them in DynamoDB (GSI1 key condition + FilterExpression), never
 * client-side. See `list-location-incidents`'s handler doc comment for why
 * that distinction matters (post-filtering after DynamoDB's `Limit` has
 * already truncated the page can silently drop matching rows).
 */
const IncidentRegister: React.FC = () => {
  // Same three-URL-shape resolution as FireDrillLogPage: flat
  // /locations/:locationId/incidents, /loc/:locId/incidents (org-admin
  // scoped in), /org/:orgId/loc/:locId/incidents (super-admin scoped in).
  const params = useParams<{ locationId?: string; locId?: string }>();
  const { activeLocationId, isInLocation, effectiveOrgId } = useLocationContext();
  const { activeRole, isSuperAdmin, isOrgAdmin } = useAuth();
  const locationId = params.locationId || params.locId || activeLocationId || undefined;

  const [recordOpen, setRecordOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity | ''>('');
  const [incidentType, setIncidentType] = useState<IncidentType | ''>('');
  const [status, setStatus] = useState<StaffIncidentStatus | ''>('');

  const { data, isLoading, isError, refetch } = useListLocationIncidentsQuery(
    {
      locationId: locationId!,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      severity: severity || undefined,
      incidentType: incidentType || undefined,
      status: status || undefined,
    },
    { skip: !locationId }
  );

  // Same statutory-overdue flag the dashboard banner reads — an org/super
  // admin scoped into this location gets LOCATION_ADMIN-shaped stats here,
  // mirroring Dashboard.tsx's own effectiveStatsRole logic.
  const effectiveStatsRole =
    isInLocation && (isSuperAdmin || isOrgAdmin) ? 'LOCATION_ADMIN' : activeRole;
  const { data: stats } = useGetDashboardStatsQuery(
    { locationId, role: effectiveStatsRole, organizationId: effectiveOrgId },
    { skip: !locationId }
  );

  if (!locationId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">
        Invalid URL
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-sm text-slate-500">
        <p className="text-base font-medium text-slate-700">
          Failed to load the incident register.
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          data-testid="incident-register-retry"
          className="text-primary-600 hover:text-primary-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  const incidents = data?.incidents ?? [];

  return (
    <div data-testid="incident-register" className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          Incident register
        </h1>
        {/* Standalone entry point: incidents don't only happen mid-session
            (arrival, collection, trips), so the register can file one without
            a live session — the modal's child-select mode; no sessionId. */}
        <button
          type="button"
          data-testid="incident-register-record-btn"
          onClick={() => setRecordOpen(true)}
          className="ml-auto inline-flex items-center gap-1 px-3 py-2 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700"
        >
          + Record incident
        </button>
      </div>

      {stats?.notifiableOverdue && (
        <div className="mb-4">
          <NotifiableOverdueBanner />
        </div>
      )}

      {/* Filters — every control pushed straight into the query, never
          applied client-side. */}
      <div
        data-testid="incident-register-filters"
        className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end"
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
          <input
            type="date"
            data-testid="incident-register-filter-from-date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-md border-gray-300 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
          <input
            type="date"
            data-testid="incident-register-filter-to-date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-md border-gray-300 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
          <select
            data-testid="incident-register-filter-severity"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as IncidentSeverity | '')}
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="">All</option>
            <option value={IncidentSeverity.MINOR}>Minor</option>
            <option value={IncidentSeverity.SIGNIFICANT}>Significant</option>
            <option value={IncidentSeverity.NOTIFIABLE}>Notifiable</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            data-testid="incident-register-filter-type"
            value={incidentType}
            onChange={(e) => setIncidentType(e.target.value as IncidentType | '')}
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="">All</option>
            {Object.values(IncidentType).map((t) => (
              <option key={t} value={t}>
                {INCIDENT_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            data-testid="incident-register-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as StaffIncidentStatus | '')}
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="">All</option>
            <option value={StaffIncidentStatus.DRAFT}>Draft</option>
            <option value={StaffIncidentStatus.SUBMITTED}>Submitted</option>
            <option value={StaffIncidentStatus.CLOSED}>Closed</option>
            <option value={StaffIncidentStatus.WITHDRAWN}>Withdrawn</option>
          </select>
        </div>
      </div>

      {incidents.length === 0 ? (
        <div
          data-testid="incident-register-empty"
          className="mt-12 text-center text-sm text-gray-500"
        >
          No incidents match these filters.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-100">
          {incidents.map((incident) => {
            const overdue = isRowOverdue(incident);
            const withdrawn = incident.status === StaffIncidentStatus.WITHDRAWN;
            return (
              <ScopedLink
                key={incident.id}
                to={`/incidents/${incident.id}`}
                data-testid={`incident-register-row-${incident.id}`}
                className={`block px-4 py-3 hover:bg-gray-50 transition-colors border-l-4 ${
                  overdue
                    ? 'border-l-rose-600 bg-rose-50'
                    : withdrawn
                    ? 'border-l-gray-300'
                    : 'border-l-transparent'
                }`}
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className="text-gray-900 font-medium whitespace-nowrap">
                    {incident.occurredAt
                      ? formatDateTime(incident.occurredAt)
                      : 'Draft — no date yet'}
                  </span>
                  <span className="text-gray-700 min-w-0 truncate">{incident.childName}</span>
                  <span className="text-gray-500 whitespace-nowrap">
                    {INCIDENT_TYPE_LABELS[incident.incidentType]}
                  </span>
                  <IncidentSeverityChip
                    severity={incident.severity}
                    testId={`incident-register-row-${incident.id}-severity`}
                  />
                  {withdrawn && (
                    <span
                      data-testid={`incident-register-row-${incident.id}-withdrawn-flag`}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700"
                    >
                      Withdrawn
                    </span>
                  )}
                  {overdue && (
                    <span
                      data-testid={`incident-register-row-${incident.id}-overdue-flag`}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-rose-600 text-white"
                    >
                      Tusla overdue
                    </span>
                  )}
                  <span className="ml-auto text-xs text-gray-400 whitespace-nowrap">
                    {incident.reportedByStaffName}
                  </span>
                </div>
                {withdrawn && incident.withdrawnReason && (
                  <p className="mt-1 text-xs text-gray-500 italic">
                    Withdrawn: {incident.withdrawnReason}
                  </p>
                )}
              </ScopedLink>
            );
          })}
        </div>
      )}

      {recordOpen && effectiveOrgId && (
        <IncidentCreateModal
          open
          onClose={() => setRecordOpen(false)}
          locationId={locationId}
          organizationId={effectiveOrgId}
        />
      )}
    </div>
  );
};

export default IncidentRegister;
